const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Default cutoff below which a background is considered dark enough to need light text. */
export const DEFAULT_DARK_THRESHOLD = 0.5;

/**
 * Whether a value is a hex color string (3, 4, 6 or 8 digits, with leading '#').
 * @param {string} color
 * @returns {boolean}
 */
export const isHexColor = (color) => HEX_COLOR.test(String(color || ''));

/**
 * Relative luminance (WCAG 2.x) of a hex color.
 * @param {string} hex - Hex color, e.g. '#7C3AED' or '#fff'.
 * @returns {number|null} Luminance between 0 (black) and 1 (white), or null if not a hex color.
 */
export const getLuminance = (hex) => {
  if (!isHexColor(hex)) return null;

  let value = hex.slice(1);
  if (value.length === 3 || value.length === 4) {
    value = value
      .slice(0, 3)
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const [r, g, b] = [0, 2, 4].map((offset) => {
    const channel = parseInt(value.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Whether a hex color is dark enough that content on top of it should be light.
 * @param {string} hex - Hex color.
 * @param {number} [threshold=DEFAULT_DARK_THRESHOLD] - Luminance cutoff; higher values treat more
 *  colors as dark.
 * @returns {boolean} False for non-hex values, so callers fall back to their default styling.
 */
export const isDarkColor = (hex, threshold = DEFAULT_DARK_THRESHOLD) => {
  const luminance = getLuminance(hex);
  return luminance === null ? false : luminance < threshold;
};
